import { useEffect, useRef, useState } from "react";
import { Marquee } from "./SitePrimitives";

function parseStat(value: string) {
  const match = /^(\D*)([\d.,]+)(.*)$/.exec(value.trim());
  if (!match) return { prefix: "", number: null as number | null, suffix: value };
  return { prefix: match[1].trim(), number: Number(match[2].replace(/,/g, "")), suffix: match[3].trim() };
}

function CountUp({ to }: { to: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(to);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    setValue(0);
    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / 1600);
          setValue(Math.round(to * (1 - Math.pow(1 - t, 3))));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [to]);
  return <span ref={ref}>{value}</span>;
}

function StatValue({ value }: { value: string }) {
  const { prefix, number, suffix } = parseStat(value);
  const small = "text-[24px] leading-[1.15] min-[810px]:text-[26px] min-[1200px]:text-[32px]";
  return (
    <p className="flex items-baseline justify-center gap-[5px] font-bold text-white [&>span]:tracking-[-0.01em]">
      {prefix ? <span className={small}>{prefix}</span> : null}
      <span className="text-[32px] leading-[1.15] min-[810px]:text-[52px]">
        {number === null ? suffix : <CountUp to={number} />}
      </span>
      {number !== null && suffix ? <span className={small}>{suffix}</span> : null}
    </p>
  );
}

const STAR_PATH =
  "M8.00627 1.35136C8.14963 1.00667 8.63792 1.00667 8.78128 1.35136L10.5155 5.52095C10.5759 5.66626 10.7126 5.76554 10.8695 5.77812L15.3709 6.139C15.743 6.16883 15.8939 6.63322 15.6104 6.87608L12.1808 9.8139C12.0613 9.91629 12.0091 10.0769 12.0456 10.23L13.0934 14.6226C13.18 14.9858 12.785 15.2728 12.4664 15.0782L8.61254 12.7243C8.47823 12.6422 8.30932 12.6422 8.17501 12.7243L4.32116 15.0782C4.00258 15.2728 3.60755 14.9858 3.69416 14.6226L4.74197 10.23C4.77848 10.0769 4.72628 9.91629 4.60676 9.8139L1.17715 6.87608C0.893638 6.63322 1.04453 6.16883 1.41665 6.139L5.91807 5.77812C6.07494 5.76554 6.2116 5.66626 6.27204 5.52095L8.00627 1.35136Z";

export function SiteStats({
  label,
  avatars = [],
  logos = [],
  items,
}: {
  label: string;
  avatars?: string[];
  logos?: Array<{ name: string; image: string }>;
  items: Array<{ value: string; label: string }>;
}) {
  if (!items.length && !logos.length) return null;
  return (
    <section data-site-section="stats" className="pb-10 pt-[60px] min-[810px]:pb-24 min-[810px]:pt-[52px] min-[1200px]:pt-20">
      <div className="flex flex-col items-center gap-2">
        {avatars.length ? (
          <div className="flex items-center">
            {avatars.map((src, index) => (
              <img
                key={src}
                src={src}
                alt=""
                className={`rounded-full object-cover ${
                  index === 1 ? "relative z-10 -mx-2 h-7 w-7" : "h-6 w-6"
                }`}
              />
            ))}
          </div>
        ) : null}
        <div className="flex gap-[2px]" aria-label="5/5">
          {Array.from({ length: 5 }).map((_, index) => (
            <svg key={index} viewBox="0 0 17 17" className="h-[17px] w-[17px]" aria-hidden="true">
              <path d={STAR_PATH} fill="var(--public-accent)" />
            </svg>
          ))}
        </div>
        {label ? (
          <p className="text-center text-[15px] font-semibold leading-[1.4] text-[#FAFAFA] min-[810px]:text-[17px] min-[1200px]:text-[18px] min-[1200px]:leading-[1.5]">
            {label}
          </p>
        ) : null}
      </div>
      {logos.length ? (
        <Marquee className="mt-8 min-[810px]:mt-12" duration={35}>
          {logos.map((logo) => (
            <div key={logo.name} className="flex h-[94px] shrink-0 items-center pr-[60px] max-[1199px]:[zoom:0.66] min-[810px]:pr-20">
              <img src={logo.image} alt={logo.name} className="h-auto max-h-[94px] w-auto" loading="lazy" />
            </div>
          ))}
        </Marquee>
      ) : null}
      {items.length ? (
        <div className="site-container mt-4 min-[810px]:mt-[14px] min-[1200px]:mt-[59px]">
          <div className="site-divider-x" />
          <div className="grid grid-cols-2 min-[1200px]:grid-cols-4">
            {items.map((item, index) => (
              <div
                key={item.value + item.label}
                className="relative flex flex-col items-center gap-2 px-6 py-6 text-center"
              >
                {index > 0 ? (
                  <span
                    className={`absolute left-0 top-0 h-full w-px bg-[linear-gradient(0deg,rgba(139,161,169,0)_0%,#374043_100%)] ${
                      index % 2 === 1 ? "" : "hidden min-[1200px]:block"
                    }`}
                  />
                ) : null}
                <StatValue value={item.value} />
                <p className="text-[14px] min-[810px]:max-w-[298px] min-[1200px]:max-w-[179px] font-medium leading-[1.2] text-white opacity-60">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
